import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { OrganisationOption } from './OrganisationOption';

const FIRST_ROW_DELAY_MS = 120;
const ROW_STAGGER_MS = 60;

type OrganisationListProps = {
  organisations: OrganisationMembership[];
  activeIndex: number | null;
  registerOption: (index: number) => Ref<HTMLButtonElement>;
  createButtonRef: Ref<HTMLButtonElement>;
  onHighlight: (index: number | null) => void;
  onOpen: (organisation: OrganisationMembership) => void;
  onCreate: () => void;
};

export function OrganisationList({
  organisations,
  activeIndex,
  registerOption,
  createButtonRef,
  onHighlight,
  onOpen,
  onCreate,
}: OrganisationListProps) {
  const { t } = useTranslation('auth');
  const tabbableIndex = activeIndex ?? 0;

  return (
    <div className="org-list">
      {organisations.length > 0 ? (
        <div role="listbox" aria-label={t('chooseOrganisation.listLabel')} className="org-list">
          {organisations.map((organisation, index) => (
            <OrganisationOption
              key={organisation.id}
              organisation={organisation}
              selected={activeIndex === index}
              tabbable={tabbableIndex === index}
              animationDelayMs={FIRST_ROW_DELAY_MS + index * ROW_STAGGER_MS}
              buttonRef={registerOption(index)}
              onHighlight={() => onHighlight(index)}
              onOpen={() => onOpen(organisation)}
            />
          ))}
        </div>
      ) : null}

      <button
        ref={createButtonRef}
        type="button"
        className="org-create"
        style={{
          animationDelay: `${FIRST_ROW_DELAY_MS + organisations.length * ROW_STAGGER_MS + 40}ms`,
        }}
        onClick={onCreate}
        onMouseEnter={() => onHighlight(null)}
      >
        <span className="org-create__icon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
        <span className="org-create__label">{t('chooseOrganisation.create')}</span>
      </button>
    </div>
  );
}
